import { type FC, useCallback, useMemo, useState } from 'react';
import { Collapse, Typography } from '@material-ui/core';
import cx from 'classnames';

// @ts-expect-error - ButtonText doesn't have TypeScript definitions
import { ButtonText } from 'src/components/ButtonText';
import { TalkingPointItem } from 'src/components/TalkingPointItem';
import { TalkingPointItemSkeleton } from 'src/components/TalkingPointItemSkeleton';
import { type TextAnnotation } from 'src/data/annotation/types';
import { type Callback } from 'src/lib/types';

// @ts-expect-error - InsightBox doesn't have TypeScript definitions
import { InsightBox } from '../InsightBox';

import styles from './styles.module.scss';

interface CustomAnnotation extends TextAnnotation {
  saved: boolean;
}

interface Props {
  classes?: Record<string, string>;
  annotations: CustomAnnotation[];
  loading: boolean;
  onSave: Callback<TextAnnotation>;
}

const TALKING_POINTS_SKELETON_ROWS = 3;

export const TalkingPoints: FC<Props> = ({ classes, annotations, loading, onSave }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandToggle = useCallback(() => {
    setExpanded((expanded) => !expanded);
  }, []);

  const defaultAnnotations = useMemo(() => {
    return annotations.slice(0, 3);
  }, [annotations]);

  const expandedAnnotations = useMemo(() => {
    return annotations.slice(3);
  }, [annotations]);

  if (!annotations.length && !loading) {
    return null;
  }

  return (
    <div className={cx(styles.root, classes?.root)}>
      <Typography className={cx(styles.boxGroupTitle, classes?.boxGroupTitle)}>
        A/B tested talking points
      </Typography>

      <InsightBox className={styles.container}>
        <div>
          {loading ? (
            <TalkingPointItemSkeleton rows={TALKING_POINTS_SKELETON_ROWS} />
          ) : (
            <>
              {defaultAnnotations.map((annotation, index) => (
                <TalkingPointItem
                  key={index}
                  talkingPoint={annotation}
                  isSaved={annotation.saved}
                  onSave={() => onSave(annotation)}
                  classes={{ name: styles.talkingPointItem_name }}
                  hideBorder={
                    index === defaultAnnotations.length - 1 && expandedAnnotations.length === 0
                  }
                />
              ))}
              <Collapse
                in={expanded}
                classes={{ wrapperInner: styles.annotationsGroups }}
              >
                {expandedAnnotations.map((annotation, index) => (
                  <TalkingPointItem
                    key={index}
                    isSaved={annotation.saved}
                    talkingPoint={annotation}
                    onSave={() => onSave(annotation)}
                    classes={{ name: styles.talkingPointItem_name }}
                  />
                ))}
              </Collapse>
            </>
          )}
        </div>

        {expandedAnnotations.length > 0 && !loading && (
          <ButtonText
            className={styles.expandButton}
            onClick={handleExpandToggle}
          >
            Show <span>{expanded ? 'less' : 'more'}</span>
          </ButtonText>
        )}
      </InsightBox>
    </div>
  );
};
